import { Link } from "react-router-dom";
import PageSEO from "@/components/feature/PageSEO";
import FMHero from "./components/FMHero";
import FMCapabilities from "./components/FMCapabilities";
import FMProcess from "./components/FMProcess";
import FMSupplyChain from "./components/FMSupplyChain";
import FMContact from "./components/FMContact";

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://chirunnexa.com";

const foundryMachiningJsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Foundry & Precision Machining | OEM Engine Block Casting China | Chirunnexa",
    "url": `${SITE_URL}/foundry-machining`,
    "description": "Premium OEM engine block casting and precision CNC machining from Guangzhou. Gray cast iron, ductile iron, reverse engineering, CMM inspection. Export to Russia, Middle East, Europe. ISO 9001:2015 certified.",
    "inLanguage": "en",
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE_URL}/` },
        { "@type": "ListItem", "position": 2, "name": "Foundry & Precision Machining", "item": `${SITE_URL}/foundry-machining` },
      ],
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "OEM Engine Block Casting & Precision Machining",
    "description": "End-to-end engine block manufacturing: gray cast iron and ductile iron casting, advanced CNC machining, CMM inspection, and global export logistics. Reverse engineering from physical samples. ISO 9001:2015 certified.",
    "url": `${SITE_URL}/foundry-machining`,
    "provider": {
      "@type": "Organization",
      "name": "Chirunnexa",
      "url": `${SITE_URL}/`,
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Guangzhou",
        "addressRegion": "Guangdong",
        "addressCountry": "CN",
      },
    },
    "serviceType": "Engine Block Casting & CNC Machining",
    "areaServed": ["Global", "RU", "AE", "DE", "PL", "KZ"],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Foundry & Machining Services",
      "itemListElement": [
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Gray Cast Iron Casting (GG25/GG30)" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Ductile Iron Casting (GGG50/GGG70)" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "5-Axis CNC Precision Machining" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "CMM Inspection & Flaw Detection" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Reverse Engineering from Physical Samples" } },
      ],
    },
  },
];

export default function FoundryMachiningPage() {
  return (
    <div style={{ background: "#fffbf5", minHeight: "100vh" }}>
      <PageSEO
        title="Foundry & Precision Machining | OEM Engine Block Casting China | Chirunnexa"
        description="Premium OEM engine block casting and precision CNC machining from Guangzhou. Gray cast iron, ductile iron, reverse engineering, CMM inspection. Export to Russia, Middle East, Europe. ISO 9001:2015 certified. B2B quote within 24h."
        keywords="engine block casting China, precision machining foundry, gray cast iron casting, ductile iron casting, OEM engine block manufacturer, reverse engineering engine block, CMM inspection foundry, CNC machining engine block"
        canonical="/foundry-machining"
        jsonLd={foundryMachiningJsonLd}
      />

      {/* Top navigation bar */}
      <nav
        className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 h-16"
        style={{
          background: "rgba(255,251,245,0.97)",
          borderBottom: "1px solid rgba(217,119,6,0.12)",
          backdropFilter: "blur(16px)",
        }}
      >
        <Link
          to="/"
          className="flex items-center gap-3 cursor-pointer"
          style={{ textDecoration: "none" }}
        >
          <div
            className="w-8 h-8 flex items-center justify-center rounded-lg"
            style={{ background: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)" }}
          >
            <i className="ri-settings-3-line text-sm text-white" />
          </div>
          <div className="flex flex-col leading-none">
            <span
              className="font-black tracking-widest"
              style={{ fontSize: "13px", letterSpacing: "0.18em", color: "#1a1a1a" }}
            >
              CHIRUNNEXA
            </span>
            <span
              className="font-light tracking-widest"
              style={{ fontSize: "8px", letterSpacing: "0.2em", color: "rgba(30,20,5,0.4)", marginTop: "2px" }}
            >
              B2B AUTO PARTS PLATFORM
            </span>
          </div>
        </Link>

        <div className="hidden md:flex items-center gap-6">
          {[
            { label: "Capabilities", href: "#fm-capabilities" },
            { label: "Our Process", href: "#fm-process" },
            { label: "Global Supply", href: "#fm-supply" },
          ].map((link) => (
            <a
              key={link.label}
              href={link.href}
              className="text-xs font-semibold tracking-widest uppercase transition-colors duration-200 whitespace-nowrap"
              style={{ color: "rgba(30,20,5,0.5)", letterSpacing: "0.14em", textDecoration: "none" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "#D97706")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(30,20,5,0.5)")}
            >
              {link.label}
            </a>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <Link
            to="/"
            className="hidden md:flex items-center gap-2 text-xs font-medium tracking-widest cursor-pointer transition-colors duration-200 whitespace-nowrap"
            style={{ color: "rgba(30,20,5,0.45)", textDecoration: "none", letterSpacing: "0.12em" }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "#D97706")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(30,20,5,0.45)")}
          >
            <i className="ri-arrow-left-line" />
            Main Site
          </Link>
          <a
            href="#fm-contact"
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-bold tracking-widest text-white transition-all duration-200 hover:brightness-110 active:scale-95 cursor-pointer whitespace-nowrap"
            style={{
              background: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)",
              letterSpacing: "0.12em",
              textDecoration: "none",
            }}
          >
            <i className="ri-mail-send-line" />
            Get Quote
          </a>
        </div>
      </nav>

      {/* Page sections */}
      <FMHero />
      <FMCapabilities />
      <div id="fm-process">
        <FMProcess />
      </div>
      <FMSupplyChain />
      <FMContact />

      {/* Footer */}
      <footer
        className="py-8 px-6"
        style={{
          background: "rgba(255,243,220,0.6)",
          borderTop: "1px solid rgba(217,119,6,0.15)",
        }}
      >
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs" style={{ color: "rgba(30,20,5,0.4)" }}>
            &copy; {new Date().getFullYear()} Chirunnexa — OEM Engine Block Casting &amp; Precision Machining | Guangzhou Export Hub
          </p>
          <div className="flex items-center gap-6">
            <Link
              to="/"
              className="text-xs transition-colors duration-200 cursor-pointer"
              style={{ color: "rgba(30,20,5,0.4)", textDecoration: "none" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "#D97706")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(30,20,5,0.4)")}
            >
              ← Back to Main Site
            </Link>
            <a
              href="mailto:chirunnexa@gmail.com"
              className="text-xs transition-colors duration-200 cursor-pointer"
              style={{ color: "rgba(30,20,5,0.4)", textDecoration: "none" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "#D97706")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(30,20,5,0.4)")}
            >
              chirunnexa@gmail.com
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
